package com.evoapps.evoshift.ui.billing

import androidx.compose.runtime.Composable
import com.evoapps.evoshift.domain.billing.BillingDuration
import com.evoapps.evoshift.domain.billing.BillingPeriod
import com.evoapps.evoshift.domain.billing.BillingStore
import com.evoapps.evoshift.domain.billing.PeriodUnit
import com.evoapps.evoshift.domain.billing.PlanLimits
import com.evoapps.evoshift.domain.billing.PlanOffer
import com.evoapps.evoshift.domain.billing.PlanTier
import com.evoapps.evoshift.domain.billing.Plans
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.duration_days
import evoshift.composeapp.generated.resources.duration_months
import evoshift.composeapp.generated.resources.duration_weeks
import evoshift.composeapp.generated.resources.duration_years
import evoshift.composeapp.generated.resources.paywall_free_trial
import evoshift.composeapp.generated.resources.paywall_go_to_plan
import evoshift.composeapp.generated.resources.paywall_intro_price
import evoshift.composeapp.generated.resources.paywall_intro_then
import evoshift.composeapp.generated.resources.paywall_price_monthly_phrase
import evoshift.composeapp.generated.resources.paywall_price_weekly_phrase
import evoshift.composeapp.generated.resources.paywall_price_yearly_phrase
import evoshift.composeapp.generated.resources.paywall_period_lifetime
import evoshift.composeapp.generated.resources.paywall_period_monthly
import evoshift.composeapp.generated.resources.paywall_period_other
import evoshift.composeapp.generated.resources.paywall_period_quarterly
import evoshift.composeapp.generated.resources.paywall_period_semiannual
import evoshift.composeapp.generated.resources.paywall_period_weekly
import evoshift.composeapp.generated.resources.paywall_period_yearly
import evoshift.composeapp.generated.resources.paywall_price_per_month
import evoshift.composeapp.generated.resources.paywall_price_per_week
import evoshift.composeapp.generated.resources.paywall_price_per_year
import evoshift.composeapp.generated.resources.plan_none
import evoshift.composeapp.generated.resources.plan_premium
import evoshift.composeapp.generated.resources.plan_trial
import evoshift.composeapp.generated.resources.paywall_seats_tier
import evoshift.composeapp.generated.resources.plan_people_count
import evoshift.composeapp.generated.resources.plan_up_to_people
import evoshift.composeapp.generated.resources.plan_unlimited
import evoshift.composeapp.generated.resources.subscription_store_app_store
import evoshift.composeapp.generated.resources.subscription_store_play
import evoshift.composeapp.generated.resources.subscription_store_promotional
import evoshift.composeapp.generated.resources.subscription_store_web
import org.jetbrains.compose.resources.pluralStringResource
import org.jetbrains.compose.resources.stringResource

/** Words for the billing facts of the domain (SH-38); the domain never formats them. */

@Composable
fun PlanTier.label(): String = when (this) {
    PlanTier.TRIAL -> stringResource(Res.string.plan_trial)
    PlanTier.PREMIUM -> stringResource(Res.string.plan_premium)
    PlanTier.NONE -> stringResource(Res.string.plan_none)
}

/** SH-220: "up to 25 people" inside a sentence ("Switch to the plan for up to 25 people"). */
@Composable
fun upToPeopleLabel(seats: Int): String = pluralStringResource(Res.plurals.plan_up_to_people, seats, seats)

/** SH-220: "11 people", a team. */
@Composable
fun peopleCountLabel(count: Int): String = pluralStringResource(Res.plurals.plan_people_count, count, count)

/** SH-220: "39 zł a month", a price in a sentence; the plain price where there is no natural unit. */
@Composable
fun BillingPeriod.pricePhrase(formattedPrice: String): String = when (this) {
    BillingPeriod.WEEKLY -> stringResource(Res.string.paywall_price_weekly_phrase, formattedPrice)
    BillingPeriod.MONTHLY -> stringResource(Res.string.paywall_price_monthly_phrase, formattedPrice)
    BillingPeriod.YEARLY -> stringResource(Res.string.paywall_price_yearly_phrase, formattedPrice)
    else -> formattedPrice
}

/**
 * SH-220: the price the first period costs: the store's discounted introductory price (the
 * launch offer) when it reports one, else the regular price. Never a number of our own.
 */
val PlanOffer.firstPrice: String
    get() = introductoryOffer?.takeIf { !it.isFreeTrial }?.formattedPrice ?: formattedPrice

/** SH-220: the primary button, "Switch to the plan for up to 10 people at 39 zł a month". */
@Composable
fun PlanOffer.goToPlanLabel(): String =
    stringResource(Res.string.paywall_go_to_plan, upToPeopleLabel(seats), period.pricePhrase(firstPrice))

/** SH-220: "Launch price for 12 months, then 69 zł a month"; null without a discounted intro price. */
@Composable
fun PlanOffer.introThenLabel(): String? {
    val intro = introductoryOffer?.takeIf { !it.isFreeTrial && it.formattedPrice != null } ?: return null
    val duration = intro.duration?.label() ?: return null
    return stringResource(Res.string.paywall_intro_then, duration, period.pricePhrase(formattedPrice))
}

/** "Up to 25 people": a seat tier. */
@Composable
fun seatsLabel(seats: Int): String = pluralStringResource(Res.plurals.paywall_seats_tier, seats, seats)

/** The free trial's length, "3 months" (or "45 days" when it is not whole months). */
@Composable
fun trialLengthLabel(): String =
    if (Plans.TRIAL_DAYS % 30 == 0) BillingDuration(Plans.TRIAL_DAYS / 30, PeriodUnit.MONTH).label()
    else BillingDuration(Plans.TRIAL_DAYS, PeriodUnit.DAY).label()

@Composable
fun BillingPeriod.label(): String = stringResource(
    when (this) {
        BillingPeriod.WEEKLY -> Res.string.paywall_period_weekly
        BillingPeriod.MONTHLY -> Res.string.paywall_period_monthly
        BillingPeriod.QUARTERLY -> Res.string.paywall_period_quarterly
        BillingPeriod.SEMIANNUAL -> Res.string.paywall_period_semiannual
        BillingPeriod.YEARLY -> Res.string.paywall_period_yearly
        BillingPeriod.LIFETIME -> Res.string.paywall_period_lifetime
        BillingPeriod.UNKNOWN -> Res.string.paywall_period_other
    }
)

/** "49,99 zł / month"; the plain price where there is no natural unit. */
@Composable
fun PlanOffer.priceLabel(): String = when (period) {
    BillingPeriod.WEEKLY -> stringResource(Res.string.paywall_price_per_week, formattedPrice)
    BillingPeriod.MONTHLY -> stringResource(Res.string.paywall_price_per_month, formattedPrice)
    BillingPeriod.YEARLY -> stringResource(Res.string.paywall_price_per_year, formattedPrice)
    else -> formattedPrice
}

/** "7 days free" or "9,99 zł for 3 months, then the regular price"; null without an intro offer. */
@Composable
fun PlanOffer.introLabel(): String? {
    val intro = introductoryOffer ?: return null
    val duration = intro.duration?.label() ?: return null
    return if (intro.isFreeTrial) stringResource(Res.string.paywall_free_trial, duration)
    else intro.formattedPrice?.let { stringResource(Res.string.paywall_intro_price, it, duration) }
}

@Composable
fun BillingDuration.label(): String = pluralStringResource(
    when (unit) {
        PeriodUnit.DAY -> Res.plurals.duration_days
        PeriodUnit.WEEK -> Res.plurals.duration_weeks
        PeriodUnit.MONTH -> Res.plurals.duration_months
        PeriodUnit.YEAR -> Res.plurals.duration_years
    },
    value, value
)

@Composable
fun BillingStore.label(): String? = when (this) {
    BillingStore.PLAY_STORE -> stringResource(Res.string.subscription_store_play)
    BillingStore.APP_STORE -> stringResource(Res.string.subscription_store_app_store)
    BillingStore.WEB -> stringResource(Res.string.subscription_store_web)
    BillingStore.PROMOTIONAL -> stringResource(Res.string.subscription_store_promotional)
    BillingStore.OTHER -> null
}

/** A limit as shown in the plan comparison: the number, or "Unlimited". */
@Composable
fun limitLabel(limit: Int): String =
    if (limit == PlanLimits.UNLIMITED) stringResource(Res.string.plan_unlimited) else limit.toString()
